/** AI B2B Leads: the selectable table of leads, shared by the saved-leads list and the search results. It shows the enrichment columns the person chose. */
import { useTranslation } from "react-i18next";
import { AlertTriangle, Linkedin } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { leadName, linkedinLabel, safeHref, type ColumnSpec, type ProspectLead } from "./types";

interface Props {
  rows: ProspectLead[];
  /** The columns to show, in order. Empty means the original set (used until the vocabulary has loaded). */
  columns: ColumnSpec[];
  loading?: boolean;
  selected: Map<string, ProspectLead>;
  onToggleRow: (lead: ProspectLead) => void;
  onTogglePage: () => void;
  emptyText: string;
}

/** Columns the table shows if it has not been told: what it always showed before columns were choosable. */
const FALLBACK: ColumnSpec[] = ["name", "title", "company", "location", "email", "phone", "linkedin", "status"].map((key) => ({ key, label: key, group: "", kind: "text" as const }));

/** A web address, or a bare domain ("acme.com", "acme.com/about") made into one. Anything else is not a link. */
function linkFor(value: string): string | null {
  if (!value) return null;
  return safeHref(value) ?? (/^(?:[a-z0-9-]+\.)+[a-z]{2,}(?:[/?#]\S*)?$/i.test(value) ? `https://${value}` : null);
}

/** What a lead has for a field: the server's `details`, or (for an older response) the typed columns the table always had. */
function valueOf(l: ProspectLead, key: string): string {
  const fromDetails = l.details?.[key];
  if (fromDetails !== undefined) return fromDetails;
  const typed: Record<string, string | null> = { title: l.title, company: l.company, location: l.location, email: l.email, phone: l.phone, linkedin: l.linkedinUrl };
  return typed[key] ?? "";
}

export function LeadsTable({ rows, columns, loading = false, selected, onToggleRow, onTogglePage, emptyText }: Props) {
  const { t } = useTranslation();
  const cols = columns.length > 0 ? columns : FALLBACK;
  const onPage = rows.filter((r) => selected.has(r.id)).length;
  const headerChecked: boolean | "indeterminate" = rows.length > 0 && onPage === rows.length ? true : onPage > 0 ? "indeterminate" : false;
  const statusLabel = (s: ProspectLead["status"]) => (s === "added" ? t("prospectLeads.leads.added") : s === "result" ? t("prospectLeads.leads.notSaved") : t("prospectLeads.leads.new"));
  const columnLabel = (c: ColumnSpec) => {
    const defaults: Record<string, string> = { name: "name", title: "title", company: "company", location: "location", phone: "phone", email: "email", linkedin: "linkedin", status: "status" };
    return c.label !== c.key ? t(`prospectLeads.columns.field.${c.key}`, { defaultValue: c.label }) : t(`prospectLeads.leads.col.${defaults[c.key] ?? c.key}`, { defaultValue: c.key });
  };

  const cell = (l: ProspectLead, c: ColumnSpec) => {
    const value = valueOf(l, c.key);
    const none = <span className="text-muted-foreground">-</span>;

    switch (c.key) {
      case "name":
        return <>{leadName(l)}{l.seniority && <Badge variant="outline" className="ml-2 text-xs">{t(`prospectLeads.filters.option.seniority.${l.seniority}`, { defaultValue: l.seniority })}</Badge>}</>;
      case "phone":
        return l.hasPhone ? (
          <span className="inline-flex flex-wrap items-center gap-1">
            {l.phone}
            {l.phoneKind === "company" && <Badge variant="outline" className="text-xs" title={t("prospectLeads.leads.companyLineHint")} data-testid="company-line-badge">{t("prospectLeads.leads.companyLine")}</Badge>}
            {!l.phoneHasCountryCode && <span title={t("prospectLeads.leads.noCountryCode")} aria-label={t("prospectLeads.leads.noCountryCode")}><AlertTriangle className="h-3.5 w-3.5 text-amber-500" /></span>}
          </span>
        ) : <Badge variant="outline" className="border-amber-400 text-amber-700 dark:text-amber-400">{t("prospectLeads.leads.noPhone")}</Badge>;
      case "status":
        return <Badge variant={l.status === "added" ? "default" : "secondary"}>{statusLabel(l.status)}</Badge>;
      case "linkedin": {
        const href = safeHref(l.linkedinUrl);
        return href ? (
          <a href={href} target="_blank" rel="noopener noreferrer" title={l.linkedinUrl!} className="inline-flex max-w-[15rem] items-center gap-1.5 text-blue-600 hover:underline dark:text-blue-400" data-testid="lead-linkedin">
            <Linkedin className="h-4 w-4 shrink-0" aria-hidden />
            <span className="truncate">{linkedinLabel(l.linkedinUrl!)}</span>
            <span className="sr-only">{t("prospectLeads.leads.linkedinOpen")}</span>
          </a>
        ) : none;
      }
    }

    if (!value) return none;
    if (c.kind === "link") {
      const href = linkFor(value);
      return href ? (
        <a href={href} target="_blank" rel="noopener noreferrer" title={value} className="inline-block max-w-[14rem] truncate align-bottom text-blue-600 hover:underline dark:text-blue-400" data-testid={`lead-link-${c.key}`}>{linkedinLabel(value)}</a>
      ) : <span className="inline-block max-w-[14rem] truncate align-bottom" title={value}>{value}</span>;
    }
    if (c.kind === "long") return <span className="line-clamp-2 max-w-[22rem] text-muted-foreground" title={value}>{value}</span>;
    return <span className={c.key === "email" ? undefined : "inline-block max-w-[16rem] truncate align-bottom"} title={value}>{value}</span>;
  };

  // room for every chosen column, so a wide choice scrolls inside the box instead of squeezing the text
  const minWidth = Math.max(640, 56 + cols.length * 150);

  return (
    <div className="overflow-x-auto rounded-lg border" data-testid="leads-table">
      <Table style={{ minWidth }}>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10"><Checkbox checked={headerChecked} onCheckedChange={onTogglePage} disabled={rows.length === 0} aria-label={t("prospectLeads.leads.selectPage")} data-testid="select-page" /></TableHead>
            {cols.map((c) => <TableHead key={c.key} data-testid={`col-${c.key}`}>{columnLabel(c)}</TableHead>)}
          </TableRow>
        </TableHeader>
        <TableBody>
          {loading && Array.from({ length: 5 }, (_, i) => (
            <TableRow key={i}>{Array.from({ length: cols.length + 1 }, (_, k) => <TableCell key={k}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>
          ))}
          {!loading && rows.length === 0 && (
            <TableRow><TableCell colSpan={cols.length + 1} className="py-10 text-center text-muted-foreground" data-testid="leads-empty">{emptyText}</TableCell></TableRow>
          )}
          {rows.map((l) => {
            const on = selected.has(l.id);
            return (
              <TableRow key={l.id} data-state={on ? "selected" : undefined} className={!l.hasPhone ? "bg-amber-50/50 dark:bg-amber-950/10" : undefined} data-testid={`lead-row-${l.id}`}>
                <TableCell><Checkbox checked={on} onCheckedChange={() => onToggleRow(l)} aria-label={t("prospectLeads.leads.selectRow", { name: leadName(l) })} /></TableCell>
                {cols.map((c) => (
                  <TableCell key={c.key} className={c.key === "name" ? "font-medium" : c.key === "title" || c.key === "location" ? "text-sm text-muted-foreground" : "text-sm"}>{cell(l, c)}</TableCell>
                ))}
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
